// StartSessionModal.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FiVideo, FiX, FiCalendar, FiClock, FiLock, FiCopy, 
  FiInfo, FiCheckCircle, FiMic, FiHeadphones, FiMonitor 
} from 'react-icons/fi';
import { BsCheckCircleFill } from 'react-icons/bs';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  /* Hide scrollbar - Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar - Firefox */
  scrollbar-width: none;
  /* Hide scrollbar - IE/Edge */
  -ms-overflow-style: none;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
  padding: 20px 24px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.div`
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
  p {
    margin: 4px 0 0 0;
    font-size: 13px;
    opacity: 0.9;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  font-weight: 600;
`;

const SessionTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0 0 16px 0;
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #555;
  font-size: 14px;

  svg {
    color: #999;
    font-size: 16px;
  }

  strong {
    color: #333;
    font-weight: 600;
  }

  .highlight {
    color: #ff4757;
  }
`;

const CounselorSection = styled(Section)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CounselorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
`;

const CounselorInfo = styled.div`
  h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    color: #333;
  }
  p {
    margin: 0;
    font-size: 14px;
    color: #777;
  }
`;

const LinkSection = styled(Section)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LinkIcon = styled.div`
  display: flex;
  align-items: center;
  color: #999;
  font-size: 18px;
`;

const MeetingLink = styled.input`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  color: #555;
  background: #fff;
  outline: none;
  cursor: pointer;
  user-select: all;

  &:focus {
    border-color: #ff4757;
  }

  &:focus-visible {
    outline: none;
  }
`;

const CopyButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #ff6b81;
  }
`;

const GuidelinesSection = styled(Section)`
  background: #e8f4fd;
  border-left: 4px solid #2196f3;
`;

const GuidelinesTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 12px;
  font-size: 15px;
`;

const GuidelinesList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #555;
  font-size: 14px;
  line-height: 1.8;

  li {
    margin-bottom: 6px;
  }
`;

const RequirementsSection = styled(Section)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #555;
  font-size: 14px;

  svg {
    color: #4caf50;
    font-size: 16px;
  }
`;

const PaymentSection = styled(Section)`
  background: #e8f5e9;
  border-left: 4px solid #4caf50;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PaymentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaymentIcon = styled.div`
  color: #4caf50;
  font-size: 20px;
`;

const PaymentText = styled.div`
  h4 {
    margin: 0 0 4px 0;
    font-size: 15px;
    color: #2e7d32;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: #666;
  }
`;

const SessionID = styled.div`
  text-align: right;
  
  p {
    margin: 0;
    font-size: 12px;
    color: #999;
  }
  strong {
    color: #555;
    font-size: 14px;
  }
`;

const ModalFooter = styled.div`
  padding: 20px 24px 24px 24px;
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  flex: 1;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #e0e0e0;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
  }
`;

const JoinButton = styled.button`
  flex: 2;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 71, 87, 0.4);
  }
`;

// Main Component
const StartSessionModal = ({ sessionData, onClose, onJoin }) => {
  const [copied, setCopied] = useState(false);

  // Format date and time from ISO string
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const options = { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    };
    const timeOptions = { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    };
    return {
      date: date.toLocaleDateString('en-US', options),
      time: date.toLocaleTimeString('en-US', timeOptions)
    };
  };

  const { date, time } = formatDateTime(sessionData.start_time);

  // const handleCopyLink = () => {
  //   // Trim whitespace from the link (handles API trailing spaces)
  //   const cleanLink = sessionData.meet_link?.trim();
    
  //   if (!cleanLink) {
  //     console.warn('No meeting link to copy');
  //     return;
  //   }

  //   navigator.clipboard.writeText(cleanLink)
  //     .then(() => {
  //       setCopied(true);
  //       setTimeout(() => setCopied(false), 2000);
  //     })
  //     .catch((err) => {
  //       console.error('Failed to copy link:', err);
  //       // Fallback for older browsers or non-HTTPS contexts
  //       const input = document.querySelector('input[readonly]');
  //       if (input) {
  //         input.select();
  //         input.setSelectionRange(0, 99999); // For mobile
  //         document.execCommand('copy');
  //         setCopied(true);
  //         setTimeout(() => setCopied(false), 2000);
  //       }
  //     });
  // };

  const handleCopyLink = async () => {
  const cleanLink = sessionData.meet_link?.trim();
  
  if (!cleanLink) {
    console.warn('No meeting link to copy');
    return;
  }

  // Modern Clipboard API (requires HTTPS/localhost)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(cleanLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    } catch (err) {
      console.error('Clipboard API failed:', err);
    }
  }

  // Fallback: Create temporary textarea (works everywhere)
  const textArea = document.createElement('textarea');
  textArea.value = cleanLink;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      console.error('Fallback copy failed');
    }
  } catch (err) {
    console.error('Fallback copy error:', err);
  } finally {
    document.body.removeChild(textArea);
  }
};


  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <HeaderContent>
            <HeaderIcon>
              <FiVideo size={20} />
            </HeaderIcon>
            <HeaderTitle>
              <h2>Ready to Join?</h2>
              <p>Your session is about to begin</p>
            </HeaderTitle>
          </HeaderContent>
          <CloseButton onClick={onClose} aria-label="Close modal">
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {/* Session Info */}
          <Section>
            <SectionTitle>Session Title</SectionTitle>
            <SessionTitle>{sessionData.session_name}</SessionTitle>
            <InfoGrid>
              <InfoItem>
                <FiCalendar />
                <span>{date}</span>
              </InfoItem>
              <InfoItem>
                <FiClock />
                <span>{time}</span>
              </InfoItem>
              <InfoItem>
                <FiClock />
                <span><strong>Duration:</strong> {sessionData.duration} minutes</span>
              </InfoItem>
              <InfoItem>
                <FiVideo />
                <span><strong>Session Type:</strong> <span className="highlight">Video Call</span></span>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Counselor Info */}
          <CounselorSection>
            <SectionTitle style={{ marginBottom: 0 }}>Your Counselor</SectionTitle>
            <CounselorImage 
              src={sessionData.creator_profile_pic?.trim()} 
              alt={sessionData.creator_name}
            />
            <CounselorInfo>
              <h4>{sessionData.creator_name}</h4>
              <p>{sessionData.session_category} Specialist</p>
            </CounselorInfo>
          </CounselorSection>

          {/* Meeting Link */}
          <LinkSection>
            <LinkIcon>
              <FiLock size={18} />
            </LinkIcon>
            <MeetingLink 
              type="text" 
              value={sessionData.meet_link?.trim() || ''} 
              readOnly 
              aria-label="Meeting link"
            />
            <CopyButton onClick={handleCopyLink} aria-label={copied ? "Link copied" : "Copy meeting link"}>
              <FiCopy size={16} />
              {copied ? 'Copied!' : 'Copy'}
            </CopyButton>
          </LinkSection>

          {/* Session Guidelines */}
          <GuidelinesSection>
            <GuidelinesTitle>
              <FiInfo size={18} />
              Session Guidelines
            </GuidelinesTitle>
            <GuidelinesList>
              <li>Ensure you're in a quiet, private space for the session</li>
              <li>Have a stable internet connection for uninterrupted counseling</li>
              <li>All sessions are end-to-end encrypted and confidential</li>
              <li>You can end the session anytime if you feel uncomfortable</li>
              <li>Please allow microphone access when prompted</li>
            </GuidelinesList>
          </GuidelinesSection>

          {/* Technical Requirements */}
          <RequirementsSection>
            <SectionTitle style={{ gridColumn: '1 / -1' }}>Technical Requirements</SectionTitle>
            <RequirementItem>
              <BsCheckCircleFill />
              <span>Stable Internet</span>
            </RequirementItem>
            <RequirementItem>
              <BsCheckCircleFill />
              <span>Modern Browser</span>
            </RequirementItem>
            <RequirementItem>
              <FiMic />
              <span>Microphone Access</span>
            </RequirementItem>
            <RequirementItem>
              <FiMonitor />
              <span>Private Space</span>
            </RequirementItem>
            <RequirementItem>
              <FiHeadphones />
              <span>Headphones (Optional)</span>
            </RequirementItem>
          </RequirementsSection>

          {/* Payment Confirmation */}
          <PaymentSection>
            <PaymentInfo>
              <PaymentIcon>
                <BsCheckCircleFill size={24} />
              </PaymentIcon>
              <PaymentText>
                <h4>Payment Confirmed</h4>
                <p>₹{sessionData.amount} paid via Payment Gateway</p>
              </PaymentText>
            </PaymentInfo>
            <SessionID>
              <p>Session ID</p>
              <strong>#{sessionData.id.toString().padStart(8, '0')}</strong>
            </SessionID>
          </PaymentSection>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <JoinButton onClick={onJoin}>
            <FiVideo size={18} />
            Join Call Session
            <span>→</span>
          </JoinButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default StartSessionModal;